import React, {useState, useEffect} from "react";
import SuiButton from '../../../../components/SoftButton';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    Typography,
    Box,
    RadioGroup,
    FormControlLabel,
    Radio,
    TextField,
    IconButton,
    Tooltip,
    CircularProgress,
    Checkbox, useTheme, useMediaQuery
} from "@mui/material";
import {getServicesToRegenerate, regenerateSelectedServices} from "../../../../axios-client";
import servicesTranslations from '../../../../constants/servicesTrasnlations.json';
import {useTranslation} from 'react-i18next';
import PropTypes from "prop-types";
import i18n from "../../../../config/i18n";
import SnackbarNotification from "../../../../reusableComponents/Snackbar/SnackbarNotification";
import {parseCustomPageRange} from "../../../../utlis/generalUtils";
import {RotateCcw} from "lucide-react";

const RegenerateServicesPopup = ({digitizationId, currentPageNum, numPages}) => {
    const {t} = useTranslation(['regenerateServices']);
    const lang = i18n.language;
    const direction = i18n.dir();
    const [open, setOpen] = useState(false);
    const [serviceOptions, setServiceOptions] = useState([]);
    const [selectedServices, setSelectedServices] = useState([]);
    const [audioOption, setAudioOption] = useState("current");
    const [pageCustom, setPageCustom] = useState("");
    const [errors, setErrors] = useState({custom: ""});
    const [isValid, setIsValid] = useState(true);
    const [loading, setLoading] = useState(false);
    const [notification, setNotification] = useState({
        show: false,
        snackbarMessage: "",
        severity: "",
        horizontal: "center"

    });

    const isAudioSelected = selectedServices.some(id => {
        const service = serviceOptions.find(option => option.id === id);
        return service?.requiresPageSelection;
    });

    useEffect(() => {
        if (digitizationId) {
            getServicesToRegenerate(digitizationId).then((res) => {
                setServiceOptions(res.data)
            }).catch((e) => {
                console.error(e)
            })
        }
    }, [digitizationId]);

    useEffect(() => {
        if (!isAudioSelected || audioOption !== "range") {
            setErrors({custom: ""});
            setIsValid(true);
            return;
        }

        const { isValid: valid, error: customError } = parseCustomPageRange(pageCustom, numPages, t);

        setErrors({custom: customError});
        setIsValid(valid);
    }, [pageCustom, audioOption, isAudioSelected, t]);

    const toggleDialog = () => {
        setOpen((prev) => !prev);
        if (open) {
            setSelectedServices([]);
            setAudioOption("current");
            setPageCustom("");
            setErrors({custom: ""});
            setIsValid(true);
        }
    };

    const handleServiceToggle = (serviceId) => {
        setSelectedServices(prev =>
            prev.includes(serviceId)
                ? prev.filter(id => id !== serviceId)
                : [...prev, serviceId]
        );
    };

    const handleConfirm = async () => {
        if (isAudioSelected && audioOption === "range" && !isValid) return;

        setLoading(true);
        const payload = {
            digitization_id: digitizationId,
            selected_services: selectedServices,
            page_nums: isAudioSelected
                ? audioOption === "range"
                    ? pageCustom
                    : audioOption === "current"
                        ? currentPageNum.toString()
                        : ''
                : ''
        };

        try {
            const res = await regenerateSelectedServices(payload);
        } catch (err) {
            console.error("Failed to trigger regenerate action:", err);
        } finally {
            setLoading(false);
            toggleDialog();
        }
    };

    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
    const isTablet = useMediaQuery(theme.breakpoints.down("md"))

    return (
        <>
            <Tooltip title={loading ? t("tooltip.regenerating") : 
                serviceOptions.length > 0 ?
                t("tooltip.regenerateServices") : t("tooltip.noServices")}
            >
                <span>
                    <IconButton
                        variant="outlined"
                        size={isMobile ? "small" : "medium"}
                        sx={{
                            height: { xs: 28, sm: 32 },
                            width: { xs: 28, sm: 32 },
                            fontSize: { xs: "0.65rem", sm: "0.75rem" },
                            color: "#616161",
                            border: "1px solid #d8d6d6",
                            borderRadius:"5px",
                            p:1,
                            "&:hover": {
                                borderColor: "#757575",
                                background: "#F5F5F5",
                            },
                        }}
                        disabled={serviceOptions.length == 0 || loading}
                        onClick={toggleDialog} style={{opacity: 1}}>
                        {loading ? (
                            <CircularProgress size={18} color="inherit"/>
                        ) : (
                            <RotateCcw size={16} />
                        )}
                    </IconButton>
                </span>
            </Tooltip>

            <Dialog open={open} onClose={toggleDialog} maxWidth="xs" fullWidth style={{direction: direction}}>
                <DialogTitle>
                    <Typography variant="h6" fontWeight="bold">
                        {t("title")}
                    </Typography>
                </DialogTitle>

                <Divider/>

                <DialogContent sx={{display: "flex", flexDirection: "column", gap: 2}}>
                    {serviceOptions.map(service => {
                        const isSelected = selectedServices.includes(service.id);
                        const serviceKey = Object.keys(servicesTranslations.en).find(
                            key =>
                                servicesTranslations[lang]?.[key] === service.label ||
                                key === service.label
                        );
                        const mappedServiceName = servicesTranslations[lang]?.[serviceKey] || service.label;
                        return (
                            <Box key={service.id} sx={{display: 'flex', flexDirection: 'column', pl: 1}}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={isSelected}
                                            onChange={() => handleServiceToggle(service.id)}
                                            sx={{
                                                mr: (direction === 'rtl' ? 0 : 1),
                                                ml: direction === 'rtl' ? 1: 0,
                                            }}
                                        />
                                    }
                                    label={mappedServiceName}
                                />

                                {isSelected && service.requiresPageSelection && (
                                    <Box
                                        sx={{
                                            pr: (direction === 'rtl' ? 3 : 0),
                                            pl: direction === 'rtl' ? 0 : 3,
                                        }}
                                    >
                                        <RadioGroup
                                            value={audioOption}
                                            onChange={(e) => setAudioOption(e.target.value)}
                                            sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                gap: 1,
                                                mt: 1,
                                            }}
                                        >
                                            <FormControlLabel value="current" control={<Radio sx={{
                                                mr: (direction === 'rtl' ? 0 : 1),
                                                ml: direction === 'rtl' ? 1 : 0,
                                            }}/>}
                                                              label={t("options.current")}/>
                                            <FormControlLabel value="all" control={<Radio sx={{
                                                mr: (direction === 'rtl' ? 0 : 1),
                                                ml: direction === 'rtl' ? 1 : 0,
                                            }}/>} label={t("options.all")}/>
                                            <FormControlLabel value="range" control={<Radio sx={{
                                                mr: (direction === 'rtl' ? 0 : 1),
                                                ml: direction === 'rtl' ? 1 : 0,
                                            }}/>}
                                                              label={t("options.range")}/>
                                        </RadioGroup>

                                        {audioOption === "range" && (
                                            <Box display="flex" gap={2} mt={1} alignItems="flex-start">
                                                <Box sx={{display: "flex", flexDirection: "column"}}>
                                                    <TextField
                                                        id="page-custom"
                                                        type="text"
                                                        size="small"
                                                        value={pageCustom}
                                                        onChange={(e) => setPageCustom(e.target.value)}
                                                        error={!!errors.custom}
                                                        helperText={errors.custom}
                                                        fullWidth
                                                    />
                                                    <small style={{fontSize: "10px"}}>
                                                        {t("rangeHint")}
                                                    </small>
                                                </Box>
                                            </Box>
                                        )}
                                    </Box>
                                )}
                            </Box>
                        );
                    })}
                </DialogContent>

                <DialogActions sx={{px: 3, pb: 2}} >
                    <SuiButton variant="contained" color="light" onClick={toggleDialog} disabled={loading}>
                        {t("buttons.cancel")}
                    </SuiButton>
                    <SuiButton
                        variant="contained"
                        color="primary"
                        onClick={handleConfirm}
                        disabled={(isAudioSelected && audioOption === "range" && !isValid) || loading}
                        sx={{ml: 1, mr:  (direction === 'rtl' ? 1 : 0),}}
                    >
                        {loading ? <CircularProgress size={20} color="inherit"/> : t("buttons.confirm")}
                    </SuiButton>
                </DialogActions>
            </Dialog>
            <SnackbarNotification
                notification={notification}
                setNotification={setNotification}
            />
        </>
    );
};

RegenerateServicesPopup.propTypes = {
    digitizationId: PropTypes.number,
    currentPageNum: PropTypes.number,
    numPages: PropTypes.number,
    fetchData: PropTypes.func
};

export default RegenerateServicesPopup;
